import React from 'react';
import { PageHero } from '../components/PageHero';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ShieldCheck, Users, Star, Facebook, Linkedin, Twitter } from 'lucide-react';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.simbaherbals.com';

export const OurStory = () => {
  const aboutPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Our Story – Simba Herbals UK',
    description: 'Pure Mufasa Energy. From the Congo rainforest to the UK—authentic African herbs for modern masculine vitality.',
    url: `${SITE_URL}/our-story`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Simba Herbals UK',
      url: SITE_URL,
      description: 'Premium 100% natural African herbal support for men\'s vitality. The Mufasa Protocol with discreet UK shipping.',
      address: { '@type': 'PostalAddress', streetAddress: '50 Victoria Street', addressLocality: 'Wolverhampton', postalCode: 'WV1 3PJ', addressCountry: 'GB' },
    },
  };

  return (
    <div className="">
      <SEO
        title="Our Story"
        description="Pure Mufasa Energy. From the Congo rainforest to the UK—authentic African herbs for modern masculine vitality. The story behind Simba Herbals UK."
        canonical="/our-story"
        jsonLd={aboutPageJsonLd}
      />
      <PageHero 
        title="The Story of Simba Herbals UK" 
        subtitle="Pure Mufasa Energy. From the Congo rainforest to the UK—authentic African herbs for modern masculine vitality."
        image="/images/services/Custom Formulations.jpg"
        height="h-[100vh]"
        waveColor="fill-white"
      />

      {/* Our Story Section */}
      <div className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute top-4 -left-4 w-full h-full border-2 border-simba-gold rounded-[2rem]"></div>
              <img 
                src="/images/pics/couple-taking-bath-valentines-day-scaled.jpg" 
                alt="Experience the power" 
                className="relative rounded-[2rem] shadow-xl z-10 w-full h-[600px] object-cover" 
              />
            </div>

            {/* Content */}
            <div className="space-y-8 order-1 lg:order-2">
              <div>
                <span className="text-simba-green font-bold text-sm uppercase tracking-widest mb-2 block">Our Story</span>
                <h2 className="font-display text-4xl lg:text-5xl text-simba-dark mb-6">The Story of Simba Herbals UK: Pure Mufasa Energy</h2>
                <div className="h-1.5 w-24 bg-simba-gold rounded-full mb-8"></div>
                <p className="text-xl text-gray-700 leading-relaxed font-medium">
                  Imagine a place where the sun never sets on your power. Deep in the heart of Africa lies the Congo Rainforest. This is a land of giants. Huge trees reach for the sky. The air is thick with life. This is where our journey begins. In the shade of the Congo, wise elders have used special plants for hundreds of years. They found roots and herbs that make a man feel strong. They called this power Mufasa Energy. We travel deep into this jungle to find the best herbs. We work with local people who know every leaf. We pick the herbs by hand. Then, we bring them to the UK. We turn these raw plants into clean, safe products. We do not add chemicals. We keep them 100% natural. nature’s                </p>
              </div>
              

            </div>
        </div>
      </div>

      {/* Vision / Mission / Motto + Stats Image */}
      <div className="py-24 bg-simba-cream">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
               
               {/* Content - Vision/Mission */}
               <div className="space-y-12">
                  
                  {/* Vision */}
                  <div className="flex gap-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0 text-simba-gold">
                         <Star size={32} />
                      </div>
                      <div>
                         <h3 className="font-display text-2xl text-simba-dark mb-2">Expertise</h3>
                         <p className="text-gray-600 leading-relaxed">Our content is written and reviewed by experts with’s 10+ years in botanical research.</p>
                      </div>
                  </div>

                  {/* Mission */}
                  <div className="flex gap-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0 text-simba-gold">
                         <ShieldCheck size={32} />
                      </div>
                      <div>
                         <h3 className="font-display text-2xl text-simba-dark mb-2">Integrity</h3>
                         <p className="text-gray-600 leading-relaxed">We strictly follow UK CAP Code rules for supplement marketing and ensure all labels are clear and honest.</p>
                      </div>
                  </div>

                   {/* Motto */}
                   <div className="flex gap-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0 text-simba-gold">
                         <Users size={32} />
                      </div>
                      <div>
                         <h3 className="font-display text-2xl text-simba-dark mb-2">Support</h3>
                         <p className="text-gray-600 leading-relaxed">We offer dedicated UK-based customer support to ensure your journey to vitality is smooth and successful.’s</p>
                      </div>
                  </div>

                  <div className="mt-10 pt-10 border-t border-gray-200">
                    <h3 className="font-display text-2xl text-simba-dark mb-4">Contact Us</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      We are here to assist the pride. Reach out to our team for any queries regarding orders, products, or the Mufasa Protocol.
                    </p>
                    <ul className="space-y-2 text-gray-600 font-medium">
                      <li><strong>WhatsApp:</strong> +44 7724 009805</li>
                      <li><strong>Email:</strong> care@simbaherbals.com</li>
                      <li><strong>Shop Address:</strong> 50 Victoria Street, Wolverhampton, WV1 3PJ, United Kingdom</li>
                    </ul>
                  </div>
               </div>

               {/* Image - Happy Couple */}
               <div className="relative">
                  <img 
                    src="/images/pics/happy-couple-eating-breakfast.jpg"
                    alt="Happy Couple Eating Breakfast"
                    className="rounded-[2rem] shadow-2xl w-full object-cover h-[500px]"
                  />
                  {/* Floating Stat Card */}
                  <div className="absolute -bottom-10 -left-10 bg-white p-8 rounded-3xl shadow-xl max-w-xs hidden md:block">
                      <div className="text-5xl font-display font-bold text-simba-gold mb-1">10+</div>
                      <div className="text-sm font-bold text-simba-dark uppercase tracking-widest">Years of Experience</div>
                  </div>
               </div>

            </div>
         </div>
      </div>

      {/* Embrace Power Banner */}
      <div className="py-32 bg-simba-dark relative overflow-hidden text-center px-6">
          <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative z-10 max-w-4xl mx-auto">
             <h2 className="font-display text-4xl lg:text-6xl text-white mb-6">Embrace Nature’s Power for a Healthier You</h2>
             <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
               Our carefully crafted herbal remedies support vitality, balance, and overall well-being, helping you thrive naturally with the power of authentic ingredients.
             </p>
             <Link to="/shop" className="inline-block bg-simba-gold text-simba-dark font-bold text-lg px-10 py-4 rounded-full hover:bg-white transition-colors shadow-lg shadow-simba-gold/20">
                Discover more
             </Link>
          </div>
      </div>

      {/* Stats Bar */}
      <div className="py-20 bg-simba-green text-white">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center divide-x divide-white/10">
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold mb-2">20+</div>
                        <div className="text-sm uppercase tracking-widest opacity-80">Holistic Products</div>
                    </div>
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold mb-2">1k+</div>
                        <div className="text-sm uppercase tracking-widest opacity-80">Happy Clients</div>
                    </div>
                     <div className="p-4">
                        <div className="text-5xl font-display font-bold mb-2">20+</div>
                        <div className="text-sm uppercase tracking-widest opacity-80">Company Support</div>
                    </div>
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold mb-2">4.7</div>
                        <div className="text-sm uppercase tracking-widest opacity-80">Client Reviews</div>
                    </div>
                </div>
            </div>
      </div>

      {/* Team Section */}
      <div className="py-24 bg-white">
         <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
            <span className="text-simba-green font-bold text-sm uppercase tracking-widest mb-2 block">Meet Our Team</span>
            <h2 className="font-display text-4xl lg:text-5xl text-simba-dark mb-16">The Experts Behind Simba Herbals</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
               {/* Team Member 1 */}
               <div className="group">
                  <div className="w-48 h-48 mx-auto bg-gray-200 rounded-full mb-6 overflow-hidden border-4 border-simba-cream group-hover:border-simba-gold transition-colors">
                     <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?fit=crop&w=300&h=300" alt="Joshua Roberts" className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-simba-dark">Joshua Roberts</h3>
                  <p className="text-sm text-simba-green uppercase tracking-wide font-bold mb-4">Founder</p>
                  <div className="flex justify-center space-x-4 text-gray-400">
                     <Facebook size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Twitter size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Linkedin size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                  </div>
               </div>

               {/* Team Member 2 */}
               <div className="group">
                  <div className="w-48 h-48 mx-auto bg-gray-200 rounded-full mb-6 overflow-hidden border-4 border-simba-cream group-hover:border-simba-gold transition-colors">
                     <img src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?fit=crop&w=300&h=300" alt="Mpho Dumeni" className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-simba-dark">Mpho Dumeni</h3>
                  <p className="text-sm text-simba-green uppercase tracking-wide font-bold mb-4">Founder</p>
                  <div className="flex justify-center space-x-4 text-gray-400">
                     <Facebook size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Twitter size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Linkedin size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                  </div>
               </div>

               {/* Team Member 3 */}
               <div className="group">
                  <div className="w-48 h-48 mx-auto bg-gray-200 rounded-full mb-6 overflow-hidden border-4 border-simba-cream group-hover:border-simba-gold transition-colors">
                     <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?fit=crop&w=300&h=300" alt="Christina Mbau" className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-simba-dark">Christina Mbau</h3>
                  <p className="text-sm text-simba-green uppercase tracking-wide font-bold mb-4">Founder</p>
                  <div className="flex justify-center space-x-4 text-gray-400">
                     <Facebook size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Twitter size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Linkedin size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                  </div>
               </div>

               {/* Team Member 4 */}
               <div className="group">
                  <div className="w-48 h-48 mx-auto bg-gray-200 rounded-full mb-6 overflow-hidden border-4 border-simba-cream group-hover:border-simba-gold transition-colors">
                     <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?fit=crop&w=300&h=300" alt="Caroline Bell" className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-simba-dark">Caroline Bell</h3>
                  <p className="text-sm text-simba-green uppercase tracking-wide font-bold mb-4">Founder</p>
                  <div className="flex justify-center space-x-4 text-gray-400">
                     <Facebook size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Twitter size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                     <Linkedin size={18} className="hover:text-simba-gold cursor-pointer transition-colors" />
                  </div>
               </div>

            </div>
         </div>
      </div>

       {/* CTA Section */}
       <div className="bg-[#D4AF37] py-24 text-center">
          <div className="max-w-3xl mx-auto px-6">
             <h2 className="font-display text-4xl lg:text-5xl text-simba-dark mb-8">Start Your Wellness Journey Today!</h2>
             <p className="text-xl text-simba-dark/80 mb-10 font-medium">
               Ready to reclaim your vitality? Our expert consultations are the perfect first step.
             </p>
             <Link to="/contact" className="inline-block bg-simba-dark text-white font-bold text-xl px-12 py-5 rounded-xl hover:bg-white hover:text-simba-dark transition-all shadow-xl hover:translate-y-[-4px]">
                Free Consultations
             </Link>
          </div>
       </div>

    </div>
  );
};
